import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import '../styles/global.css';

const STEPS = ['Your Info', 'Choose Plan', 'Payment', 'Confirm'];

// True only when a plan has a real retail comparison and positive monthly savings.
const hasRealSavings = (plan) =>
  plan != null &&
  plan.retail_price != null &&
  parseFloat(plan.retail_price) > parseFloat(plan.bulknet_price) &&
  parseFloat(plan.savings_per_month || 0) > 0;

const StepBar = ({ current }) => (
  <div className="step-bar">
    {STEPS.map((label, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 0 }}>
        <div className="step">
          <div className={`step-dot ${i < current ? 'done' : i === current ? 'active' : 'future'}`}>
            {i < current ? '✓' : i + 1}
          </div>
          <div className={`step-label ${i === current ? 'active' : ''}`}>{label}</div>
        </div>
        {i < STEPS.length - 1 && <div className={`step-line${i < current ? ' done' : ''}`} />}
      </div>
    ))}
  </div>
);

export default function SignupPage() {
  const { propertySlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState(0);
  const [property, setProperty] = useState(null);
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Service Agreement (e-signature consent) state
  const [riderHtml, setRiderHtml] = useState('');
  const [riderContentHash, setRiderContentHash] = useState('');
  const [riderLoading, setRiderLoading] = useState(false);
  const [riderError, setRiderError] = useState(null);
  const [showRider, setShowRider] = useState(false); // expanded/collapsed
  const [orderFormConsent, setOrderFormConsent] = useState(false);
  const [tcpaMarketingConsent, setTcpaMarketingConsent] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm();

  // Pre-fill unit from invite link
  const inviteUnit = searchParams.get('unit');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/plans/property/${propertySlug}`);
        setProperty(res.data.property);
        setPlans(res.data.plans);
        setUnits(res.data.available_units);
        if (inviteUnit) setValue('unit_number', inviteUnit);
      } catch {
        toast.error('Property not found or unavailable.');
      } finally {
        setPageLoading(false);
      }
    };
    load();
  }, [propertySlug, inviteUnit, setValue]);

  // Upgrade modal — ESC to close + lock body scroll while open
  useEffect(() => {
    if (!showUpgradeModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowUpgradeModal(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showUpgradeModal]);

  // Fetch the Service Agreement rider when the tenant reaches the Payment step
  useEffect(() => {
    if (step !== 2 || !selectedPlan) return;
    let cancelled = false;
    setRiderLoading(true);
    setRiderError(null);
    api.post('/consents/preview', {
      property_slug: propertySlug,
      unit_number: formData.unit_number,
      plan_id: selectedPlan.id,
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
    })
      .then((res) => {
        if (cancelled) return;
        setRiderHtml(res.data.rider_html || '');
        setRiderContentHash(res.data.content_hash || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setRiderError(err.response?.data?.error || err.message || 'Unknown error');
      })
      .finally(() => { if (!cancelled) setRiderLoading(false); });
    return () => { cancelled = true; };
  }, [step, selectedPlan, propertySlug, formData]);

  // Step 1 — collect info
  const onInfoSubmit = (data) => {
    setFormData(data);
    setStep(1);
  };

  // Step 2 — plan selected
  const onPlanSelect = (plan) => {
    setSelectedPlan(plan);
    setStep(2);
  };

  // Step 3 — payment & create account
  const onPaymentSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);

    try {
      // 1. Register tenant
      const regRes = await api.post('/auth/tenant/register', {
        ...formData,
        property_slug: propertySlug,
        signup_method: inviteUnit ? 'landlord_invite' : 'self',
        rider_html: riderHtml,
        rider_content_hash: riderContentHash,
        order_form_consent: orderFormConsent,
        tcpa_marketing_consent: tcpaMarketingConsent,
        email_consent: true, // they signed up via email, implicit
        plan_id: selectedPlan.id,
        monthly_services_fee: selectedPlan.bulknet_price,
      });
      const { token, tenant } = regRes.data;

      // Store token temporarily for next API calls
      localStorage.setItem('bulknet_token', token);

      // 2. Get Stripe setup intent
      const intentRes = await api.post('/billing/tenant/setup-intent');
      const { client_secret } = intentRes.data;

      // 3. Confirm card setup
      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${formData.first_name} ${formData.last_name}`,
            email: formData.email,
          },
        },
      });

      if (error) throw new Error(error.message);

      // 4. Create subscription
      await api.post('/billing/tenant/subscribe', {
        plan_id: selectedPlan.id,
        payment_method_id: setupIntent.payment_method,
      });

      // 5. Log in
      login(token, tenant, 'tenant');
      setStep(3);
      toast.success('Account created! Welcome to Affordable Telecom.');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Signup failed. Please try again.');
      localStorage.removeItem('bulknet_token');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  // Double Play plan for this property, if one exists (used by the upsell gate + modal)
  const doublePlay = plans.find((p) => p.tier === 'double');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      {/* Hero */}
      <div className="hero-bg" style={{ padding: '32px 24px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em', marginBottom: 8 }}>
          {property?.address?.toUpperCase()} · {property?.city?.toUpperCase()}, {property?.state?.toUpperCase()}
        </div>
        <h1 style={{ color: 'white', fontSize: 28, fontWeight: 800, marginBottom: 8 }}>
          Get connected — and save.
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, marginBottom: 28 }}>
          Locked-in rates, reduced-rate TV, and protection from Spectrum's annual increases — through your building's partnership with Affordable Telecom.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, paddingBottom: 28, flexWrap: 'wrap' }}>
          {['📡 Internet', '📺 + Cable', '📞 + Phone'].map((p, i) => (
            <div key={i} style={{
              background: i === 0 ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
              color: i === 0 ? 'var(--navy)' : 'white',
              borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 600
            }}>{p}</div>
          ))}
        </div>
      </div>

      {/* Form area */}
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 20px' }}>
        {step < 3 && <StepBar current={step} />}

        {/* ── Step 0: Info ── */}
        {step === 0 && (
          <form onSubmit={handleSubmit(onInfoSubmit)}>
            <div className="two-col">
              <div className="form-group">
                <label className="form-label" htmlFor="first_name">First name</label>
                <input id="first_name" className={`form-input${errors.first_name ? ' error' : ''}`} {...register('first_name', { required: true })} />
                {errors.first_name && <div className="form-error">Required</div>}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="last_name">Last name</label>
                <input id="last_name" className={`form-input${errors.last_name ? ' error' : ''}`} {...register('last_name', { required: true })} />
                {errors.last_name && <div className="form-error">Required</div>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-email">Email address</label>
              <input id="signup-email" type="email" className={`form-input${errors.email ? ' error' : ''}`} {...register('email', { required: true })} />
              {errors.email && <div className="form-error">Valid email required</div>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone number</label>
              <input id="phone" className="form-input" {...register('phone')} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="unit_number">Unit number</label>
              <select id="unit_number" className={`form-input${errors.unit_number ? ' error' : ''}`} {...register('unit_number', { required: true })}>
                <option value="">Select your unit</option>
                {units.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              {errors.unit_number && <div className="form-error">Please select your unit</div>}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="signup-password">Create a password</label>
              <input id="signup-password" type="password" className={`form-input${errors.password ? ' error' : ''}`}
                {...register('password', { required: true, minLength: 8 })} />
              {errors.password && <div className="form-error">Minimum 8 characters</div>}
            </div>
            <button type="submit" className="btn btn-primary btn-full">Continue to Plan Selection →</button>
            <p style={{ fontSize: 11, color: 'var(--text-sub)', textAlign: 'center', marginTop: 12 }}>
              By continuing you agree to our{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>Terms of Service</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>Privacy Policy</a>.
            </p>
          </form>
        )}

        {/* ── Step 1: Plan selection ── */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Choose your plan</h2>
            <p style={{ color: 'var(--text-sub)', fontSize: 13, marginBottom: 20 }}>
              All plans include 24/7 tenant support and dedicated Affordable Telecom account management.
            </p>
            <div className="plan-grid">
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={`plan-card${i === 1 ? ' popular' : ''}${selectedPlan?.id === plan.id ? ' selected' : ''}`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  {i === 1 && <div className="plan-badge">Most popular</div>}
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-price">${plan.bulknet_price}<span>/mo</span></div>
                  {plan.retail_price != null && parseFloat(plan.retail_price) > parseFloat(plan.bulknet_price) && (
                    <div className="plan-retail">Compare to ${parseFloat(plan.retail_price).toFixed(0)} retail</div>
                  )}
                  {hasRealSavings(plan) && (
                    <div className="plan-savings">Save ${parseFloat(plan.savings_per_month || 0)}/mo ({parseFloat(plan.savings_pct || 0)}% off)</div>
                  )}
                  <div className="plan-features">
                    {(Array.isArray(plan.features) ? plan.features : []).map((f, fi) => (
                      <div key={fi} className="plan-feature">{f}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {hasRealSavings(selectedPlan) && (
              <div className="savings-banner">
                <div>
                  <div className="savings-text">Your selected savings</div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>{selectedPlan.name} vs. retail pricing</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="savings-amount">${(parseFloat(selectedPlan.savings_per_year || 0)).toFixed(0)}/yr</div>
                  <div className="savings-label">saved annually</div>
                </div>
              </div>
            )}

            {/* Price-match notice */}
            <div style={{
              background: '#f0f7ff', borderLeft: '4px solid var(--navy)', borderRadius: 8,
              padding: 16, margin: '16px 0',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
                💡 Currently paying less than this to Spectrum?
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6 }}>
                Submit 3 months of recent Spectrum statements to info@affordable-telecom.com after signup, and we'll review your rate. If eligible, we'll match it so you won't pay more than your current Spectrum promotional rate for as long as that promo is in effect.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" onClick={() => setStep(0)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: 13 }}
                disabled={!selectedPlan} onClick={() => {
                  if (selectedPlan?.tier === 'single' && doublePlay) {
                    setShowUpgradeModal(true);
                  } else {
                    setStep(2);
                  }
                }}>
                Continue to Payment →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Payment ── */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>Payment details</h2>
            <p style={{ color: 'var(--text-sub)', fontSize: 13, marginBottom: 20 }}>
              Your card will be charged ${selectedPlan?.bulknet_price}/mo starting today.
            </p>

            {/* Order summary */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><div className="card-title">Order summary</div></div>
              <div className="card-body">
                <div className="flex-between" style={{ marginBottom: 8 }}>
                  <span>{selectedPlan?.name}</span>
                  <strong>${selectedPlan?.bulknet_price}/mo</strong>
                </div>
                {hasRealSavings(selectedPlan) && (
                  <>
                    <div className="flex-between" style={{ color: 'var(--text-sub)', fontSize: 12 }}>
                      <span>Retail price</span>
                      <span style={{ textDecoration: 'line-through' }}>${selectedPlan?.retail_price}/mo</span>
                    </div>
                    <div className="divider" />
                    <div className="flex-between">
                      <span style={{ color: 'var(--green)', fontWeight: 600 }}>Your savings</span>
                      <span style={{ color: 'var(--green)', fontWeight: 700 }}>
                        -${parseFloat(selectedPlan?.savings_per_month || 0).toFixed(2)}/mo
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Card input */}
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><div className="card-title">Card details</div></div>
              <div className="card-body">
                <div style={{ padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <CardElement options={{
                    style: {
                      base: { fontSize: '14px', color: 'var(--text)', '::placeholder': { color: 'var(--muted)' } }
                    }
                  }} />
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-sub)', marginTop: 10 }}>
                  🔒 Payments are secured by Stripe. Affordable Telecom never stores your card details.
                </p>
              </div>
            </div>

            {/* Service Agreement — e-signature consent */}
            <div style={{ marginTop: 32, marginBottom: 24, padding: 20, border: '1px solid #e0e0e0', borderRadius: 8, backgroundColor: '#fafafa' }}>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 18 }}>Service Agreement</h3>

              {riderLoading && <p>Loading agreement...</p>}
              {riderError && <p style={{ color: '#c00' }}>Could not load agreement: {riderError}</p>}

              {riderHtml && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowRider(!showRider)}
                    style={{
                      background: 'none', border: 'none', color: 'var(--navy)',
                      textDecoration: 'underline', cursor: 'pointer', padding: 0,
                      fontSize: 14, marginBottom: 16
                    }}
                  >
                    {showRider ? '▼ Hide full agreement' : '▶ View full agreement'}
                  </button>

                  {showRider && (
                    <div
                      style={{
                        maxHeight: 400,
                        overflowY: 'auto',
                        padding: 16,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        backgroundColor: 'white',
                        marginBottom: 16,
                        fontSize: 13
                      }}
                      dangerouslySetInnerHTML={{ __html: riderHtml }}
                    />
                  )}

                  {/* Required consent — Order Form */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={orderFormConsent}
                      onChange={(e) => setOrderFormConsent(e.target.checked)}
                      style={{ marginTop: 4, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 14, lineHeight: 1.4 }}>
                      <strong>I have read and agree</strong> to the Service Agreement above, including the monthly Services Fee, equipment terms, and that this agreement is separate from my Residential Lease. <span style={{ color: '#c00' }}>(Required)</span>
                    </span>
                  </label>

                  {/* Optional consent — TCPA marketing */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={tcpaMarketingConsent}
                      onChange={(e) => setTcpaMarketingConsent(e.target.checked)}
                      style={{ marginTop: 4, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 14, lineHeight: 1.4, color: '#555' }}>
                      I agree to receive occasional marketing calls, SMS, and emails from Affordable Telecom about service updates, promotions, and new offerings. I understand I can opt out at any time. <span style={{ fontStyle: 'italic' }}>(Optional)</span>
                    </span>
                  </label>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" onClick={() => setStep(1)}>← Back</button>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: 13 }}
                onClick={onPaymentSubmit} disabled={loading || !stripe || !orderFormConsent || !riderHtml}>
                {loading ? <><div className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} />Processing...</> : `Confirm & Pay $${selectedPlan?.bulknet_price}/mo`}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Confirmation ── */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', marginBottom: 8 }}>You're all set!</h2>
            <p style={{ color: 'var(--text-sub)', marginBottom: 8 }}>
              Your {selectedPlan?.name} plan is active. Welcome to Affordable Telecom!
            </p>
            {hasRealSavings(selectedPlan) && (
              <div className="savings-banner" style={{ textAlign: 'left', maxWidth: 400, margin: '20px auto' }}>
                <div>
                  <div className="savings-text">You're saving every month</div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>vs. {selectedPlan?.isp_name || 'retail'} pricing</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="savings-amount">${parseFloat(selectedPlan?.savings_per_month || 0).toFixed(2)}/mo</div>
                  <div className="savings-label">${parseFloat(selectedPlan?.savings_per_year || 0).toFixed(0)}/yr</div>
                </div>
              </div>
            )}
            <button className="btn btn-primary" onClick={() => navigate('/tenant/dashboard')} style={{ padding: '13px 32px' }}>
              Go to My Dashboard →
            </button>
            <p style={{ fontSize: 12, color: 'var(--text-sub)', marginTop: 12 }}>
              A welcome email has been sent to {formData.email}
            </p>
          </div>
        )}
      </div>

      {/* ── Last-chance Double Play upsell modal ── */}
      {showUpgradeModal && doublePlay && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowUpgradeModal(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            style={{
              position: 'relative', background: '#fff', borderRadius: 12,
              width: '90%', maxWidth: 500, padding: 32,
              maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowUpgradeModal(false)}
              style={{
                position: 'absolute', top: 14, right: 16, background: 'none',
                border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--text-sub)',
              }}
            >
              ✕
            </button>

            <h2 id="upgrade-modal-title" style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)', margin: '0 0 8px' }}>
              Wait — are you sure?
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text-sub)', margin: '0 0 20px', lineHeight: 1.6 }}>
              Upgrade to Double Play for just $18 more — and save $176+/mo vs. Spectrum's $262 retail.
            </p>

            {/* Plan comparison */}
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 24, background: 'var(--light-bg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>Single Play</div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>your current selection</div>
                </div>
                <strong style={{ fontSize: 16, color: 'var(--navy)' }}>${parseFloat(selectedPlan.bulknet_price).toFixed(2)}/mo</strong>
              </div>
              <div className="divider" />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
                    Double Play <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)' }}>recommended</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>includes Spectrum TV Select Plus + 1 Xumo Box</div>
                </div>
                <strong style={{ fontSize: 16, color: 'var(--navy)' }}>${parseFloat(doublePlay.bulknet_price).toFixed(2)}/mo</strong>
              </div>
              <div className="divider" />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '6px 0' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>Annual savings vs. Spectrum retail</span>
                <strong style={{ fontSize: 15, color: 'var(--green)' }}>${parseFloat(doublePlay.savings_per_year || 0).toFixed(0)}/yr</strong>
              </div>
            </div>

            <button
              type="button"
              className="btn btn-primary btn-full"
              style={{ justifyContent: 'center', padding: 14, background: '#1a2138' }}
              onClick={() => { setSelectedPlan(doublePlay); setShowUpgradeModal(false); setStep(2); }}
            >
              Upgrade to Double Play
            </button>
            <button
              type="button"
              onClick={() => { setShowUpgradeModal(false); setStep(2); }}
              style={{
                display: 'block', width: '100%', marginTop: 12, padding: 4,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, color: 'var(--text-sub)', textDecoration: 'underline',
              }}
            >
              No thanks, stay with Single Play
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
